import React, { type PropsWithChildren, useState } from "react";

import TriangleDown from "assets/triangleDownBlack.svg";
import TriangleUp from "assets/triangleUpBlack.svg";
import type { PreviewNotes } from "~types/types";

import styles from "./InfoCard.module.css";

export enum InfoCardType {
    error = "error",
    valid = "valid",
    default = "default",
}

interface InfoCardProps {
    expanded?: boolean;
    title: string;
    message?: string;
    cardType?: InfoCardType;
    emoji?: string;
    previewNotes?: PreviewNotes;
}

export const NotesAndLinksCard: React.FC<PropsWithChildren<InfoCardProps>> = (
    props
) => {
    const { links = [], description, warning } = props.previewNotes || {};

    return (
        <div className={styles.variantInfo}>
            {links.length > 0 &&
                links.map(({ name, url }, i) => (
                    <a href={url} target="_blank" key={i}>
                        🔗 {name}
                    </a>
                ))}
            {description && (
                <p className={styles.notes}>
                    <strong>Notes:</strong> {description}
                </p>
            )}
            {warning && <p className={styles.warning}>{warning}</p>}
        </div>
    );
};

export const InfoCard: React.FC<PropsWithChildren<InfoCardProps>> = (props) => {
    const {
        expanded = false,
        title,
        message,
        cardType = InfoCardType.default,
        children,
        emoji,
        previewNotes = {},
    } = props;

    const [isContainerOpen, setContainerOpen] = useState(expanded);

    const toggleCollapsibleContainer = () => {
        setContainerOpen(!isContainerOpen);
    };

    let cardTypeClass = styles.default;

    if (cardType === InfoCardType.error) {
        cardTypeClass = styles.error;
    }

    if (cardType === InfoCardType.valid) {
        cardTypeClass = styles.valid;
    }

    return (
        <div className={styles.card}>
            <div className={cardTypeClass}>
                <button
                    onClick={toggleCollapsibleContainer}
                    className={styles.titleAndToggle}
                >
                    <div className={styles.toggle}>
                        {isContainerOpen ? <TriangleDown /> : <TriangleUp />}
                    </div>
                    <div className={styles.title}>
                        {title}
                        {emoji && <span className={styles.emoji}>{emoji}</span>}
                    </div>
                </button>
                <div
                    className={styles.collapsibleContainer}
                    data-expanded={isContainerOpen ? "true" : "false"}
                >
                    {message && (
                        <div className={styles.optionalMessage}>{message}</div>
                    )}
                    {children}
                    {previewNotes && <NotesAndLinksCard {...props} />}
                </div>
            </div>
        </div>
    );
};
